import React, { useState, useEffect, useContext, useCallback } from 'react';
import Layout from '../../components/layouts/Main';
import { AuthContext } from '../../context/AuthContext';
import { FaTimes, FaWarehouse, FaBox, FaArrowRight, FaExchangeAlt } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../../config';
import axios from 'axios';

const NewStockTransfer = () => {
  const { loggedUser: user } = useContext(AuthContext);
  const domainName = window.location.hostname;
  const apiUrl = config.API_BASE_URL;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    productId: '',
    sourceBatch: null,
    destinationWarehouseId: '',
    quantity: '',
    noOfBags: '',
    outerNoOfBags: '',
    remark: ''
  });

  const [products, setProducts] = useState([]);
  const [availableBatches, setAvailableBatches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingStock, setFetchingStock] = useState(false);
  const [submissionLoading, setSubmissionLoading] = useState(false);

  // Fetch all products
  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/products`, {
        headers: { 'X-Subdomain': domainName },
        withCredentials: true
      });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    }
  }, [apiUrl, domainName]);

  // Fetch all warehouses
  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/new-warehouses/storage`, {
        headers: { 'X-Subdomain': domainName },
        withCredentials: true
      });
      setWarehouses(response.data || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      toast.error('Failed to load warehouses');
    }
  }, [apiUrl, domainName]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchProducts(), fetchWarehouses()]).finally(() => setLoading(false));
  }, [fetchProducts, fetchWarehouses]);

  // Fetch available stock batches when product changes
  useEffect(() => {
    const fetchStock = async () => {
      if (!formData.productId) {
        setAvailableBatches([]);
        return;
      }

      setFetchingStock(true);
      try {
        const response = await axios.get(`${apiUrl}/new-stock-transfer/available-stock/${formData.productId}`, {
          headers: { 'X-Subdomain': domainName },
          withCredentials: true
        });
        if (response.data.success) {
          setAvailableBatches(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching available stock:', error);
        toast.error('Failed to load available stock');
      } finally {
        setFetchingStock(false);
      }
    };

    fetchStock();
  }, [formData.productId, apiUrl, domainName]);

  // Handle Product Change
  const handleProductChange = (e) => {
    setFormData({
      ...formData,
      productId: e.target.value,
      sourceBatch: null,
      quantity: '',
      noOfBags: '',
      outerNoOfBags: ''
    });
  };

  // Handle Batch Selection
  const handleBatchChange = (e) => {
    const batchIndex = e.target.value;
    if (batchIndex === "") {
      setFormData({ ...formData, sourceBatch: null, quantity: '', noOfBags: '', outerNoOfBags: '' });
      return;
    }

    const selectedBatch = availableBatches[batchIndex];
    setFormData({
      ...formData,
      sourceBatch: selectedBatch,
      quantity: '',
      noOfBags: '',
      outerNoOfBags: ''
    });
  };

  // Calculate Bags when Quantity changes
  const handleQuantityChange = (e) => {
    const qty = e.target.value;
    let bags = '';
    let outerBags = '';

    if (qty && formData.sourceBatch) {
      const batch = formData.sourceBatch;
      
      // Calculate Inner Bags
      if (batch.quantityPerBag > 0) {
        bags = Math.ceil(qty / batch.quantityPerBag);
      }

      // Calculate Outer Bags
      if (bags && batch.bagsPerOuterBag > 0) {
        outerBags = Math.ceil(bags / batch.bagsPerOuterBag);
      } else if (bags && batch.outerPackingMaterial?.outerPackingCapacity > 0) {
        outerBags = Math.ceil(bags / batch.outerPackingMaterial.outerPackingCapacity);
      }
    }

    setFormData({
      ...formData,
      quantity: qty,
      noOfBags: bags,
      outerNoOfBags: outerBags
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.sourceBatch || !formData.destinationWarehouseId || !formData.quantity) {
      toast.error('Please fill all required fields');
      return;
    }

    if (parseFloat(formData.quantity) > formData.sourceBatch.availableQuantity) {
      toast.error('Quantity exceeds available stock');
      return;
    }

    if (formData.sourceBatch.warehouse._id === formData.destinationWarehouseId) {
      toast.error('Source and destination warehouses cannot be the same');
      return;
    }

    setSubmissionLoading(true);
    try {
      const batch = formData.sourceBatch;
      const payload = {
        productId: formData.productId,
        sourceWarehouseId: batch.warehouse._id,
        destinationWarehouseId: formData.destinationWarehouseId,
        quantity: parseFloat(formData.quantity),
        packingMaterialId: batch.packingMaterial?._id,
        outerPackingMaterialId: batch.outerPackingMaterial?._id,
        noOfBags: parseInt(formData.noOfBags) || 0,
        outerNoOfBags: parseInt(formData.outerNoOfBags) || 0,
        quantityPerBag: batch.quantityPerBag,
        bagsPerOuterBag: batch.bagsPerOuterBag,
        batchNumber: batch.batchNumber,
        purchaseOrderId: batch.purchaseOrder?._id,
        grnId: batch.grn,
        unit: batch.unit,
        quality: batch.quality,
        remark: formData.remark
      };

      const response = await axios.post(`${apiUrl}/new-stock-transfer/execute`, payload, {
        headers: { 
            'X-Subdomain': domainName,
            'user': user?._id || ''
        },
        withCredentials: true
      });

      if (response.data.success) {
        toast.success('Stock transfer completed successfully');
        navigate('/inventory');
      } else {
        toast.error(response.data.message || 'Failed to execute transfer');
      }
    } catch (error) {
      console.error('Error executing transfer:', error);
      toast.error(error.response?.data?.message || 'Failed to execute transfer');
    } finally {
      setSubmissionLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4">
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
            <li className="breadcrumb-item"><Link to="/inventory">Inventory</Link></li>
            <li className="breadcrumb-item active">New Stock Transfer</li>
          </ol>
        </nav>

        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-primary text-white py-3">
                <h5 className="mb-0 d-flex align-items-center">
                  <FaExchangeAlt className="me-2" />
                  New Stock Transfer (Simple)
                </h5>
              </div>
              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="row g-4">
                    {/* Step 1: Product Selection */}
                    <div className="col-md-6">
                      <div className="card h-100 border-light bg-light bg-opacity-10">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-3 text-muted d-flex align-items-center">
                            <FaBox className="me-2" /> Step 1: Select Product
                          </h6>
                          <div className="mb-3">
                            <label className="form-label fw-bold">Product *</label>
                            <select
                              className="form-select border-primary"
                              value={formData.productId}
                              onChange={handleProductChange}
                              required
                            >
                              <option value="">-- Select Product --</option>
                              {products.filter(p => p.isActive).map(p => (
                                <option key={p._id} value={p._id}>{p.productName} ({p.productCode})</option>
                              ))}
                            </select>
                          </div>

                          {formData.productId && (
                            <div className="mb-3">
                              <label className="form-label fw-bold">Select Source Stock Batch *</label>
                              {fetchingStock ? (
                                <div className="text-center py-2">
                                  <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                </div>
                              ) : (
                                <select
                                  className="form-select border-primary"
                                  onChange={handleBatchChange}
                                  required
                                >
                                  <option value="">-- Select Available Stock --</option>
                                  {availableBatches.filter(b => b.availableQuantity > 0).map((b, index) => (
                                    <option key={index} value={index}>
                                      {b.warehouse.label} ({b.availableQuantity} {b.unit}) 
                                      {b.packingMaterial ? ` | ${b.packingMaterial.name} (${b.availableBags} bags)` : ' | Unpacked'}
                                      {b.outerPackingMaterial ? ` | ${b.outerPackingMaterial.name} (${b.availableOuterBags} outer bags)` : ''}
                                    </option>
                                  ))}
                                </select>
                              )}
                              {availableBatches.length === 0 && !fetchingStock && formData.productId && (
                                <div className="text-danger small mt-1">No stock available for this product.</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Step 2: Destination Selection */}
                    <div className="col-md-6">
                      <div className="card h-100 border-light bg-light bg-opacity-10">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-3 text-muted d-flex align-items-center">
                            <FaWarehouse className="me-2" /> Step 2: Destination
                          </h6>
                          <div className="mb-3">
                            <label className="form-label fw-bold">Destination Location *</label>
                            <select
                              className="form-select border-primary"
                              value={formData.destinationWarehouseId}
                              onChange={(e) => setFormData({ ...formData, destinationWarehouseId: e.target.value })}
                              required
                            >
                              <option value="">-- Select Destination --</option>
                              {warehouses.map(w => (
                                <option key={w._id} value={w._id}>{w.fullPath || w.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 3: Transfer Details */}
                    {formData.sourceBatch && (
                      <div className="col-12">
                        <div className="card border-primary border-opacity-25 bg-primary bg-opacity-10">
                          <div className="card-body">
                            <h6 className="card-subtitle mb-3 text-muted">Step 3: Transfer Details</h6>
                            
                            <div className="row g-3">
                              <div className="col-md-3">
                                <label className="form-label fw-bold">Quantity to Move *</label>
                                <div className="input-group">
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={formData.quantity}
                                    onChange={handleQuantityChange}
                                    max={formData.sourceBatch.availableQuantity}
                                    min="0.01"
                                    step="0.01"
                                    placeholder="Enter quantity"
                                    required
                                  />
                                  <span className="input-group-text">{formData.sourceBatch.unit}</span>
                                </div>
                                <div className="form-text">Available: {formData.sourceBatch.availableQuantity} {formData.sourceBatch.unit}</div>
                              </div>

                              <div className="col-md-3">
                                <label className="form-label fw-bold">No. of Bags</label>
                                <input
                                  type="number"
                                  className="form-control bg-light"
                                  value={formData.noOfBags}
                                  onChange={(e) => setFormData({...formData, noOfBags: e.target.value})}
                                />
                                <div className="form-text">Stuffing: {formData.sourceBatch.quantityPerBag} {formData.sourceBatch.unit}/bag</div>
                              </div>

                              <div className="col-md-3">
                                <label className="form-label fw-bold">Outer No. of Bags</label>
                                <input
                                  type="number"
                                  className="form-control bg-light"
                                  value={formData.outerNoOfBags}
                                  onChange={(e) => setFormData({...formData, outerNoOfBags: e.target.value})}
                                />
                                {formData.sourceBatch.outerPackingMaterial && (
                                  <div className="form-text">Capacity: {formData.sourceBatch.outerPackingMaterial.outerPackingCapacity} inner/outer</div>
                                )}
                              </div>

                              <div className="col-md-3">
                                <label className="form-label fw-bold">Quality</label>
                                <input
                                  type="text"
                                  className="form-control bg-light"
                                  value={formData.sourceBatch.quality || 'N/A'}
                                  readOnly
                                />
                              </div>

                              <div className="col-12 mt-3">
                                <label className="form-label fw-bold">Remark</label>
                                <textarea
                                  className="form-control"
                                  rows="2"
                                  value={formData.remark}
                                  onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                                  placeholder="Enter any notes for this transfer"
                                ></textarea>
                              </div>
                            </div>

                            {/* Batch Info Summary */}
                            <div className="mt-4 p-3 bg-white rounded-3 border">
                              <div className="row g-3">
                                <div className="col-md-4">
                                  <small className="text-muted d-block">Packing Material</small>
                                  <span>{formData.sourceBatch.packingMaterial?.name || 'Unpacked'}</span>
                                </div>
                                <div className="col-md-4">
                                  <small className="text-muted d-block">Batch Number</small>
                                  <span>{formData.sourceBatch.batchNumber || 'N/A'}</span>
                                </div>
                                <div className="col-md-4">
                                  <small className="text-muted d-block">PO Number</small>
                                  <span>{formData.sourceBatch.purchaseOrder?.poNumber || 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                    <button
                      type="button"
                      className="btn btn-outline-secondary me-2 px-4"
                      onClick={() => navigate('/inventory')}
                    >
                      <FaTimes className="me-1" /> Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary px-5"
                      disabled={submissionLoading || !formData.sourceBatch || !formData.destinationWarehouseId || !formData.quantity}
                    >
                      {submissionLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Transferring...
                        </>
                      ) : (
                        <>
                          <FaArrowRight className="me-1" /> Execute Transfer
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NewStockTransfer;
